import React, { useState, useEffect } from 'react';
import { auth } from '../../lib/firebase';
import { createListing, getListingById } from '../../services/listings';
import { createTicketLink } from '../../services/ticketLinks';
import { sellerProductAddedEmail } from '../../services/email';
import { compressImage } from '../../utils/imageCompress';
import { NIGERIAN_UNIVERSITIES } from '../../data/nigerianUniversities';

interface CreateListingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (listingId: string) => void;
  initialType?: 'item' | 'house' | 'ticket';
}

const CreateListingModal: React.FC<CreateListingModalProps> = ({ isOpen, onClose, onCreated, initialType = 'item' }) => {
  const [newListing, setNewListing] = useState({
    title: '',
    price: '',
    category: '',
    condition: '',
    location: '',
    description: ''
  });
  const [specs, setSpecs] = useState<Array<{ key: string; value: string }>>([
    { key: '', value: '' },
  ]);
  const [listingImages, setListingImages] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // New: support listing types and custom categories. House listing is "coming soon",
  // so never let the modal sit on it — fall back to a general item.
  const [listingType, setListingType] = useState<'item' | 'house' | 'ticket'>(initialType === 'house' ? 'item' : initialType);
  const [customCategory, setCustomCategory] = useState('');
  const [ticketUniversity, setTicketUniversity] = useState('');

  useEffect(() => {
    if (listingType === 'house') setListingType('item');
  }, [listingType]);

  // Sync the form type each time the modal opens (e.g. "Sell Tickets" vs "Create Listing").
  useEffect(() => {
    if (isOpen) setListingType(initialType === 'house' ? 'item' : initialType);
  }, [isOpen, initialType]);

  // House-specific fields
  const [houseDetails, setHouseDetails] = useState({ type: '', bedrooms: '', bathrooms: '', amenities: '' });
  const [houseFees, setHouseFees] = useState<Array<{ name: string; amount: string }>>([
    { name: '', amount: '' },
  ]);

  // Ticket-specific fields
  const [ticketDetails, setTicketDetails] = useState({ eventDate: '', venue: '', ticketType: '', quantity: '' });
  const [ticketTiers, setTicketTiers] = useState<Array<{ name: string; price: string; quantity: string; perks?: string }>>([
    { name: '', price: '', quantity: '', perks: '' },
  ]);

  // Delivery/Pickup options - uncomment and initialize these
  const [deliveryOption, setDeliveryOption] = useState(false);
  const [pickupOption, setPickupOption] = useState(false);
  const [pickupAddress, setPickupAddress] = useState('');

  useEffect(() => {
    if (listingType === 'ticket') {
      const prices = ticketTiers.map(t => Number(t.price)).filter(p => !isNaN(p) && p > 0);
      const base = prices.length ? Math.min(...prices) : '';
      setNewListing(prev => ({ ...prev, price: base ? String(base) : '' }));
    }
  }, [listingType, ticketTiers]);

  // Dynamic placeholders and labels per listing type
  const titlePlaceholder = listingType === 'house'
    ? 'e.g., 1BR Apartment near Campus'
    : listingType === 'ticket'
    ? 'e.g., Freshers Concert Ticket'
    : 'e.g., Calculus Textbook (2nd Edition)';

  const pricePlaceholder = listingType === 'house'
    ? 'e.g., 120000 (per month)'
    : listingType === 'ticket'
    ? 'Auto from tiers'
    : 'e.g., 5000';

  const locationPlaceholder = listingType === 'house'
    ? 'e.g., Akure, North Gate, Block 3'
    : listingType === 'ticket'
    ? 'e.g., Main Auditorium'
    : 'e.g., FUTA North Gate';

  const descriptionLabel = listingType === 'ticket' ? 'Ticket Details' : 'Description';
  const descriptionPlaceholder = listingType === 'house'
    ? 'Include rent terms, utilities, and amenities'
    : listingType === 'ticket'
    ? 'Include event date, venue, delivery method, and restrictions'
    : 'Provide details, condition, and meet-up location';
 
  // Validation: for tickets, require at least one tier with name and positive price
  const isTicketValid = listingType !== 'ticket'
    ? true
    : ticketTiers.some(t => t.name.trim() && !isNaN(Number(t.price)) && Number(t.price) > 0);

  const handleListingImagesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length) {
      // Snapshot the files synchronously. The functional setState updater runs after
      // this handler returns — by then `e.target.value = ''` has already cleared the
      // input's FileList, so reading `e.target.files` lazily inside the updater would
      // add nothing on the first upload. Capture the array first, then reset.
      const picked = Array.from(e.target.files);
      setListingImages((prev) => [...prev, ...picked]);
      e.target.value = ''; // allow re-selecting the same file
    }
  };

  const removeListingImage = (index: number) => {
    setListingImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Object-URL previews for the selected files; revoked when the selection changes/unmounts.
  const imagePreviews = React.useMemo(
    () => listingImages.map((f) => ({ url: URL.createObjectURL(f), isVideo: f.type.startsWith('video/'), name: f.name })),
    [listingImages]
  );
  useEffect(() => () => { imagePreviews.forEach((p) => { try { URL.revokeObjectURL(p.url); } catch {} }); }, [imagePreviews]);

  const handleCreateListing = async (e: React.FormEvent) => {
    e.preventDefault();
    setUploadError(null);

    // Guard: ensure ticket tiers are valid when posting tickets
    if (listingType === 'ticket' && !isTicketValid) {
      setUploadError('Add at least one ticket tier with a name and positive price.');
      return;
    }

    try {
      const user = auth.currentUser;
      if (!user) {
        setUploadError('Please login to post a listing.');
        return;
      }

      const specificationObj: Record<string, string> = {};
      specs.filter(s => s.key.trim() && s.value.trim()).forEach(s => {
        specificationObj[s.key.trim()] = s.value.trim();
      });

      // Determine effective category and merge type-specific details into specifications
      const effectiveCategory = listingType === 'item'
        ? (customCategory.trim() || newListing.category || undefined)
        : (listingType === 'house' ? 'housing' : 'tickets');

      if (listingType === 'item' && !effectiveCategory) {
        setUploadError('Please select or enter a category.');
        return;
      }

      const specifications: Record<string, string> = { ...specificationObj };

      if (listingType === 'house') {
        if (houseDetails.type) specifications['houseType'] = houseDetails.type;
        if (houseDetails.bedrooms) specifications['bedrooms'] = String(houseDetails.bedrooms);
        if (houseDetails.bathrooms) specifications['bathrooms'] = String(houseDetails.bathrooms);
        if (houseDetails.amenities) specifications['amenities'] = houseDetails.amenities;
        houseFees.filter(f => f.name.trim() && f.amount.trim()).forEach((f, idx) => {
          specifications[`fee_${idx + 1}_${f.name.trim()}`] = String(f.amount);
        });
      }

      if (listingType === 'ticket') {
         if (ticketDetails.eventDate) specifications['eventDate'] = ticketDetails.eventDate;
         if (ticketDetails.venue) specifications['venue'] = ticketDetails.venue;
         if (ticketDetails.ticketType) specifications['ticketType'] = ticketDetails.ticketType;
         if (ticketDetails.quantity) specifications['quantity'] = String(ticketDetails.quantity);
       }

      // Validate pickup address when pickup is enabled
      if (listingType === 'item' && pickupOption && !pickupAddress.trim()) {
        setUploadError('Please provide a pickup address when enabling pickup.');
        return;
      }
 
      const listingData = {
        title: newListing.title.trim(),
        price: Number(newListing.price),
        category: effectiveCategory,
        condition: newListing.condition || undefined,
        location: newListing.location || undefined,
        description: newListing.description || undefined,
        specifications,
        university: listingType === 'ticket' && ticketUniversity.trim() ? ticketUniversity.trim() : undefined,
        type: listingType,
        deliveryOption: listingType === 'item' ? deliveryOption : false,
        pickupOption: listingType === 'item' ? pickupOption : false,
        pickupAddress: listingType === 'item' && pickupOption ? pickupAddress.trim() : undefined,
        ticketTiers: listingType === 'ticket'
          ? ticketTiers.filter(t => t.name.trim() && t.price.trim()).map(t => ({
              name: t.name.trim(),
              price: Number(t.price),
              quantity: Number(t.quantity || '0'),
              perks: t.perks?.trim() || undefined,
            }))
          : undefined,
      } as any;

      setUploading(true);

      // Compress photos in the browser before upload. A raw phone photo can be several
      // MB, which exceeds the serverless request-body limit (~4.5MB) and makes posting
      // appear frozen. Videos/non-images pass through unchanged.
      const processedImages = await Promise.all(listingImages.map((f) => compressImage(f)));

      // Guard files that are still too large to upload (e.g. long videos) so the user
      // gets a clear message instead of a stuck "Posting..." button.
      const oversized = processedImages.find((f) => f.size > 4 * 1024 * 1024);
      if (oversized) {
        setUploading(false);
        setUploadError(`"${oversized.name}" is ${(oversized.size / 1024 / 1024).toFixed(1)}MB — too large to upload. Please use a photo or video under 4MB.`);
        return;
      }

      const id = await createListing(listingData, processedImages, user);

      // Notify seller via email about product addition
      try {
        if (user.email) {
          await sellerProductAddedEmail(user.email, listingData.title);
        }
      } catch (e) {
        console.warn('Failed to send seller product-added email', e);
      }

      // If posting a ticket, also generate a rich Ticket Link buyers can pay via (guest checkout).
      if (listingType === 'ticket') {
        const totalTierQty = (ticketTiers || []).filter(t => t.name.trim()).reduce((sum, t) => sum + (Number(t.quantity || '0') || 0), 0);
        const maxQuantity = totalTierQty > 0 ? totalTierQty : (Number(ticketDetails.quantity || '0') || undefined);
        const tierPayload = (ticketTiers || [])
          .filter(t => t.name.trim() && String(t.price).trim())
          .map(t => ({
            name: t.name.trim(),
            price: Number(t.price) || 0,
            quantity: Number(t.quantity || '0') || undefined,
            perks: t.perks?.trim() || undefined,
          }));
        try {
          const created = await getListingById(id).catch(() => null);
          await createTicketLink({
            sellerId: user.uid,
            sellerName: user.displayName || undefined,
            sellerEmail: user.email || undefined,
            title: listingData.title,
            price: Number(listingData.price || 0),
            description: listingData.description,
            maxQuantity,
            tiers: tierPayload.length ? tierPayload : undefined,
            eventDate: ticketDetails.eventDate || undefined,
            venue: ticketDetails.venue || undefined,
            university: ticketUniversity.trim() || undefined,
            images: created?.images && created.images.length ? created.images : undefined,
            listingId: id,
          });
        } catch (e) {
          console.error('Failed to create ticket link:', e);
        }
      }

      setUploading(false);

      setListingImages([]);
      setNewListing({ title: '', price: '', category: '', condition: '', location: '', description: '' });
      setSpecs([{ key: '', value: '' }]);
      setPickupAddress('');
      onClose();
      if (onCreated) onCreated(id);
    } catch (err: any) {
      console.error('Failed to create listing:', err);
      setUploading(false);
      setUploadError(err?.message || 'Failed to create listing.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e)=>e.stopPropagation()}>
        <div className="sticky top-0 p-4 border-b border-gray-200 flex justify-between items-center bg-white">
          <h3 className="text-lg font-semibold text-gray-900">
            {listingType === 'house' ? 'Post House Listing' : listingType === 'ticket' ? 'Sell Ticket' : 'Post New Item'}
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
            <span className="sr-only">Close</span>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <form id="createListingForm" onSubmit={handleCreateListing} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Delivery/Pickup Options (items only) */}
          {listingType === 'item' && (
            <>
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="deliveryOption"
                    checked={deliveryOption}
                    onChange={(e) => setDeliveryOption(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="deliveryOption" className="ml-2 block text-sm text-gray-900">
                    Delivery Available
                  </label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="pickupOption"
                    checked={pickupOption}
                    onChange={(e) => setPickupOption(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                  />
                  <label htmlFor="pickupOption" className="ml-2 block text-sm text-gray-900">
                    Pickup Available
                  </label>
                </div>
              </div>

              {pickupOption && (
                <div className="mt-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address</label>
                  <input
                    type="text"
                    value={pickupAddress}
                    onChange={(e) => setPickupAddress(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg p-2"
                    placeholder="e.g., FUTA North Gate, Shop 12"
                  />
                  <p className="text-xs text-gray-500 mt-1">Provide the exact address or landmark where buyers can pick up.</p>
                </div>
              )}
            </>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              name="title"
              value={newListing.title}
              onChange={(e)=>setNewListing(prev=>({...prev, title: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg p-2"
              placeholder={titlePlaceholder}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Price (₦)</label>
              <input
                type="number"
                name="price"
                value={newListing.price}
                onChange={(e)=>setNewListing(prev=>({...prev, price: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg p-2"
                placeholder={pricePlaceholder}
                readOnly={listingType === 'ticket'}
                required={listingType !== 'ticket'}
              />
              {listingType === 'ticket' && (
                <p className="text-xs text-gray-500 mt-1">Base price is lowest tier price.</p>
              )}
            </div>
            {/* Category or Custom Category (dependent on listing type) */}
            {listingType === 'item' ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <select
                    name="category"
                    className="w-full border border-gray-300 rounded-lg p-2"
                    value={newListing.category}
                    onChange={(e) => setNewListing(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="">Select category</option>
                    <option value="textbooks">Textbooks</option>
                    <option value="electronics">Electronics</option>
                    <option value="fashion">Fashion</option>
                    <option value="others">Others</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Or enter a custom category"
                    className="w-full border border-gray-300 rounded-lg p-2"
                    value={customCategory}
                    onChange={(e) => setCustomCategory(e.target.value)}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">If custom category is provided, it will be used.</p>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <input
                  type="text"
                  readOnly
                  value={listingType === 'house' ? 'housing' : 'tickets'}
                  className="w-full border border-gray-300 rounded-lg p-2 bg-gray-100 text-gray-600"
                />
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {listingType !== 'ticket' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                <select
                  name="condition"
                  value={newListing.condition}
                  onChange={(e)=>setNewListing(prev=>({...prev, condition: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg p-2"
                  required
                >
                  <option value="">Select condition</option>
                  <option value="new">New</option>
                  <option value="used - like new">Used - Like New</option>
                  <option value="used - good">Used - Good</option>
                  <option value="used - acceptable">Used - Acceptable</option>
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                type="text"
                name="location"
                value={newListing.location}
                onChange={(e)=>setNewListing(prev=>({...prev, location: e.target.value}))}
                className="w-full border border-gray-300 rounded-lg p-2"
                placeholder={locationPlaceholder}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{descriptionLabel}</label>
            <textarea
              name="description"
              value={newListing.description}
              onChange={(e)=>setNewListing(prev=>({...prev, description: e.target.value}))}
              className="w-full border border-gray-300 rounded-lg p-2"
              rows={4}
              placeholder={descriptionPlaceholder}
            />
          </div>

          {/* House specific inputs */}
          {listingType === 'house' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">House Type</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg p-2"
                  value={houseDetails.type}
                  onChange={(e)=>setHouseDetails(prev=>({ ...prev, type: e.target.value }))}
                  placeholder="e.g., Apartment, Studio"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                <input
                  type="number"
                  className="w-full border border-gray-300 rounded-lg p-2"
                  value={houseDetails.bedrooms}
                  onChange={(e)=>setHouseDetails(prev=>({ ...prev, bedrooms: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                <input
                  type="number"
                  className="w-full border border-gray-300 rounded-lg p-2"
                  value={houseDetails.bathrooms}
                  onChange={(e)=>setHouseDetails(prev=>({ ...prev, bathrooms: e.target.value }))}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Amenities</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg p-2"
                  value={houseDetails.amenities}
                  onChange={(e)=>setHouseDetails(prev=>({ ...prev, amenities: e.target.value }))}
                  placeholder="e.g., WiFi, Parking, Water"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Other Fees</label>
                <div className="space-y-2">
                  {houseFees.map((fee, idx) => (
                    <div key={idx} className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        className="border border-gray-300 rounded-lg p-2"
                        placeholder="Fee name (e.g., Caution)"
                        value={fee.name}
                        onChange={(e)=>{
                          const next = [...houseFees];
                          next[idx] = { ...next[idx], name: e.target.value };
                          setHouseFees(next);
                        }}
                      />
                      <input
                        type="number"
                        className="border border-gray-300 rounded-lg p-2"
                        placeholder="Amount"
                        value={fee.amount}
                        onChange={(e)=>{
                          const next = [...houseFees];
                          next[idx] = { ...next[idx], amount: e.target.value };
                          setHouseFees(next);
                        }}
                      />
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <button type="button" className="px-3 py-1 text-sm bg-gray-100 rounded" onClick={()=>setHouseFees(prev=>[...prev, { name: '', amount: '' }])}>Add Fee</button>
                    {houseFees.length > 1 && (
                      <button type="button" className="px-3 py-1 text-sm bg-gray-100 rounded" onClick={()=>setHouseFees(prev=>prev.slice(0,-1))}>Remove Fee</button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ticket specific inputs */}
          {listingType === 'ticket' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">School / University</label>
                <input
                  list="ticket-university-list"
                  className="w-full border border-gray-300 rounded-lg p-2"
                  value={ticketUniversity}
                  onChange={(e) => setTicketUniversity(e.target.value)}
                  placeholder="Type to search your school…"
                />
                <datalist id="ticket-university-list">
                  {NIGERIAN_UNIVERSITIES.map((u) => <option key={u} value={u} />)}
                </datalist>
                <p className="text-xs text-gray-500 mt-1">Buyers can find your event under this campus.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Date</label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg p-2"
                  value={ticketDetails.eventDate}
                  onChange={(e)=>setTicketDetails(prev=>({ ...prev, eventDate: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Venue</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg p-2"
                  value={ticketDetails.venue}
                  onChange={(e)=>setTicketDetails(prev=>({ ...prev, venue: e.target.value }))}
                  placeholder="e.g., Main Auditorium"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Type</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg p-2"
                  value={ticketDetails.ticketType}
                  onChange={(e)=>setTicketDetails(prev=>({ ...prev, ticketType: e.target.value }))}
                  placeholder="e.g., VIP, Regular"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Available</label>
                <input
                  type="number"
                  className="w-full border border-gray-300 rounded-lg p-2"
                  value={ticketDetails.quantity}
                  onChange={(e)=>setTicketDetails(prev=>({ ...prev, quantity: e.target.value }))}
                />
              </div>
              <div className="md:col-span-2">
                <p className="text-xs text-gray-500">Tickets are delivered digitally; buyer receives confirmation and can be messaged for QR/code delivery.</p>
              </div>
            </div>
          )}

          {/* Ticket tiers */}
          {listingType === 'ticket' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Tiers</label>
              {ticketTiers.map((t, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                  <input
                    type="text"
                    className="border border-gray-300 rounded-lg p-2"
                    placeholder="Tier name (e.g., VIP, Regular)"
                    value={t.name}
                    onChange={(e)=>{
                      const next = [...ticketTiers];
                      next[idx] = { ...next[idx], name: e.target.value };
                      setTicketTiers(next);
                    }}
                  />
                  <input
                    type="number"
                    className="border border-gray-300 rounded-lg p-2"
                    placeholder="Price"
                    value={t.price}
                    onChange={(e)=>{
                      const next = [...ticketTiers];
                      next[idx] = { ...next[idx], price: e.target.value };
                      setTicketTiers(next);
                    }}
                  />
                  <input
                    type="number"
                    className="border border-gray-300 rounded-lg p-2"
                    placeholder="Quantity"
                    value={t.quantity}
                    onChange={(e)=>{
                      const next = [...ticketTiers];
                      next[idx] = { ...next[idx], quantity: e.target.value };
                      setTicketTiers(next);
                    }}
                  />
                  <input
                    type="text"
                    className="border border-gray-300 rounded-lg p-2"
                    placeholder="Perks (optional)"
                    value={t.perks || ''}
                    onChange={(e)=>{
                      const next = [...ticketTiers];
                      next[idx] = { ...next[idx], perks: e.target.value };
                      setTicketTiers(next);
                    }}
                  />
                </div>
              ))}
              <div className="flex gap-2">
                <button type="button" className="px-3 py-1 text-sm bg-gray-100 rounded" onClick={()=>setTicketTiers(prev=>[...prev, { name: '', price: '', quantity: '', perks: '' }])}>Add Tier</button>
                {ticketTiers.length > 1 && (
                  <button type="button" className="px-3 py-1 text-sm bg-gray-100 rounded" onClick={()=>setTicketTiers(prev=>prev.slice(0,-1))}>Remove Tier</button>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">Lowest tier price auto-fills the main price.</p>
            </div>
          )}

          {listingType === 'item' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Specifications</label>
              <div className="space-y-2">
                {specs.map((s, idx) => (
                  <div key={idx} className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={s.key}
                      onChange={(e)=>{
                        const next = [...specs];
                        next[idx] = { ...next[idx], key: e.target.value };
                        setSpecs(next);
                      }}
                      placeholder="Label (e.g., Capacity)"
                      className="border border-gray-300 rounded-lg p-2"
                    />
                    <input
                      type="text"
                      value={s.value}
                      onChange={(e)=>{
                        const next = [...specs];
                        next[idx] = { ...next[idx], value: e.target.value };
                        setSpecs(next);
                      }}
                      placeholder="Value (e.g., 20,000 mAh)"
                      className="border border-gray-300 rounded-lg p-2"
                    />
                  </div>
                ))}
                <div className="flex gap-2">
                  <button type="button" onClick={()=>setSpecs(prev=>[...prev, { key: '', value: '' }])} className="px-3 py-1 text-sm bg-gray-100 rounded">Add Row</button>
                  {specs.length > 1 && (
                    <button type="button" onClick={()=>setSpecs(prev=>prev.slice(0, -1))} className="px-3 py-1 text-sm bg-gray-100 rounded">Remove Row</button>
                  )}
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Photos</label>
            <label htmlFor="listingImagesModal" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <i className="ri-image-line text-3xl text-gray-400"></i>
                <p className="mb-1 text-sm text-gray-500"><span className="font-semibold">Click to upload</span> or drag and drop</p>
                <p className="text-xs text-gray-500">PNG, JPG, GIF, MP4, MOV, AVI, WMV (Max 5MB per file)</p>
              </div>
              <input
                id="listingImagesModal"
                type="file"
                multiple
                className="hidden"
                accept="image/*,video/*"
                onChange={handleListingImagesChange}
              />
            </label>
            {listingImages.length > 0 && (
              <p className="text-sm text-green-600 mt-2">{listingImages.length} file(s) selected — tap ✕ to remove any</p>
            )}
            {imagePreviews.length > 0 && (
              <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                {imagePreviews.map((p, i) => (
                  <div key={i} className="group relative h-20 w-full overflow-hidden rounded-lg bg-gray-100 ring-1 ring-gray-200">
                    {p.isVideo ? (
                      <video src={p.url} muted playsInline className="h-full w-full object-cover" />
                    ) : (
                      <img src={p.url} alt={p.name} className="h-full w-full object-cover" />
                    )}
                    <button
                      type="button"
                      onClick={() => removeListingImage(i)}
                      aria-label="Remove"
                      className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white transition-colors hover:bg-red-600"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                    {p.isVideo && <span className="absolute bottom-1 left-1 rounded bg-black/60 px-1 text-[9px] text-white"><i className="ri-film-line"></i></span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}

          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={uploading || (listingType === 'ticket' && !isTicketValid)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-60">
              {uploading ? 'Posting...' : 'Post Listing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateListingModal;